import { useState, useEffect } from "react";
import {
  X,
  UserCheck,
  Check,
  Users,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isSuperAdmin, getUserCompanies } from "../../lib/permissions";

export default function ReassignTaskModal({ task, user, onClose, onReassigned }) {
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const taskCompany = (task.company_name || "Pinaki Solutions").trim();

  // Normalize list of current assignees on this task
  const currentAssignees = Array.isArray(task.assignees) && task.assignees.length > 0
    ? task.assignees
    : [
        {
          user_id: task.original_assigned_to_id || task.assigned_to_id,
          name: task.original_assigned_to_name || task.assigned_to_name || "Assignee",
          email: task.assigned_to_email || "",
          department: task.department || "General",
          is_original: true,
        },
      ];

  const originalAssigneeName =
    task.original_assigned_to_name ||
    task.assigned_to_name ||
    currentAssignees.find((a) => a.is_original)?.name ||
    currentAssignees[0]?.name ||
    "Original Assignee";

  // Load eligible active users from the same organization
  useEffect(() => {
    let active = true;

    async function loadEligibleUsers() {
      setLoadingUsers(true);
      setError("");
      try {
        // Try dedicated task eligible-users endpoint first
        const res = await api("get", `/tasks/${task.task_id}/eligible-users`);
        if (active && Array.isArray(res.data)) {
          setEligibleUsers(res.data);
          setLoadingUsers(false);
          return;
        }
      } catch (err) {
        console.warn("Falling back to /users?active_only=true", err);
      }

      // Fallback to /users
      try {
        const res = await api("get", "/users?active_only=true");
        if (active && Array.isArray(res.data)) {
          const currentAssigneeIds = new Set(task.assigned_to_ids || []);
          if (task.assigned_to_id) currentAssigneeIds.add(task.assigned_to_id);
          (task.assignees || []).forEach((a) => a.user_id && currentAssigneeIds.add(a.user_id));

          const taskCompLower = taskCompany.toLowerCase();
          const userComps = getUserCompanies(user).map((c) => c.toLowerCase());

          const filtered = res.data.filter((u) => {
            if (u.is_active === false || u.is_deleted) return false;
            if (currentAssigneeIds.has(u.user_id)) return false;

            if (isSuperAdmin(user)) return true;

            const uComps = getUserCompanies(u).map((c) => c.toLowerCase());
            const matchesTask = uComps.includes(taskCompLower) || (taskCompLower === "pinaki solutions" && uComps.length === 0);
            const matchesUser = uComps.some((c) => userComps.includes(c));
            return matchesTask || matchesUser;
          });

          // Sort alphabetically
          filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setEligibleUsers(filtered);
        }
      } catch (fallbackErr) {
        if (active) {
          setError("Failed to load eligible users for assignment.");
        }
      } finally {
        if (active) setLoadingUsers(false);
      }
    }

    loadEligibleUsers();

    return () => {
      active = false;
    };
  }, [task, user, taskCompany]);

  const selectedUser = eligibleUsers.find((u) => u.user_id === selectedUserId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      setError("Please select an eligible user to assign this task to.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const res = await api("post", `/tasks/${task.task_id}/assignees`, {
        user_id: selectedUserId,
        note: reason.trim(),
      });

      const newName = selectedUser?.name || "the selected user";
      toast.success(`Task #${task.task_id} successfully assigned to ${newName}! Both assignees can now view and report on this task.`);
      if (onReassigned) {
        onReassigned(res.data);
      }
      onClose(true);
    } catch (err) {
      console.error("Assignment error:", err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to assign task. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }} data-testid="reassign-task-backdrop">
      <div
        className="modal"
        style={{ maxWidth: 560, width: "95%", maxHeight: "90vh", overflowY: "auto" }}
        data-testid="reassign-task-modal"
      >
        {/* Modal Header */}
        <div className="modal-head" style={{ marginBottom: 14 }}>
          <div>
            <div className="small-label" style={{ color: "#2563eb", letterSpacing: "1px" }}>
              MULTI-USER TASK ASSIGNMENT
            </div>
            <h2 style={{ fontSize: 18, marginTop: 4, color: "var(--navy)" }}>
              Assign to Another User
            </h2>
            <p className="muted" style={{ fontSize: 12, margin: "2px 0 0" }}>
              Task #{task.task_id} · <span style={{ color: "#1e293b", fontWeight: 600 }}>{task.title}</span>
            </p>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={() => !busy && onClose(false)}
            data-testid="reassign-modal-close-btn"
            disabled={busy}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} data-testid="reassign-task-form">
          {/* Current Assignees Summary */}
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>
                <Users size={13} color="#2563eb" />
                <span>Current Assignees ({currentAssignees.length})</span>
              </div>
              <span className="badge blue" style={{ fontSize: 10 }}>
                {taskCompany}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {currentAssignees.map((a, idx) => (
                <div
                  key={a.user_id || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: a.is_original ? "var(--navy)" : "#3b82f6",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      {(a.name || "U").trim().charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--navy)", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {a.name || "User"}
                      </div>
                      {a.email && (
                        <div style={{ fontSize: 11, color: "#64748b", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                          {a.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {a.department && (
                      <span className="badge slate" style={{ fontSize: 10 }}>
                        {a.department}
                      </span>
                    )}
                    {a.is_original ? (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 6px",
                          borderRadius: 4,
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          border: "1px solid #bfdbfe",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <ShieldCheck size={10} /> Original Assignee
                      </span>
                    ) : (
                      <span className="badge amber" style={{ fontSize: 10 }}>
                        Co-Assignee
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <p style={{ margin: "10px 0 0", fontSize: 11.5, color: "#475569", lineHeight: 1.45 }}>
              💡 The original assignee (<b>{originalAssigneeName}</b>) will remain assigned. Both users will see this task in their respective Tasks & Reports views.
            </p>
          </div>

          {/* New Assignee Selector */}
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                color: "#1e293b",
                marginBottom: 6,
              }}
            >
              Select Additional Assignee <span style={{ color: "var(--red)" }}>*</span>
            </label>

            {loadingUsers ? (
              <div
                style={{
                  padding: "12px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "#f8fafc",
                  border: "1.5px dashed #cbd5e1",
                  borderRadius: 8,
                  fontSize: 12.5,
                  color: "#64748b",
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    border: "2px solid #94a3b8",
                    borderTopColor: "#2563eb",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <span>Loading eligible team members from {taskCompany}...</span>
              </div>
            ) : eligibleUsers.length === 0 ? (
              <div
                style={{
                  padding: "12px 14px",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 8,
                  fontSize: 12.5,
                  color: "#92400e",
                }}
              >
                No other active team members found in <b>{taskCompany}</b> eligible for task assignment.
              </div>
            ) : (
              <select
                className="dispatch-control"
                value={selectedUserId}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                  setError("");
                }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 13,
                  borderRadius: 8,
                  border: selectedUserId ? "1.5px solid #2563eb" : "1.5px solid #cbd5e1",
                  background: "#ffffff",
                  outline: "none",
                }}
                required
                data-testid="reassign-user-select"
              >
                <option value="">-- Choose Additional Assignee --</option>
                {eligibleUsers.map((u) => {
                  const dept = u.department || (Array.isArray(u.departments) ? u.departments[0] : "General");
                  const comp = u.company || (Array.isArray(u.companies) ? u.companies[0] : taskCompany);
                  return (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name || u.email || "User"} — {dept} ({comp})
                    </option>
                  );
                })}
              </select>
            )}
            <small style={{ display: "block", marginTop: 4, fontSize: 11.5, color: "#64748b" }}>
              Only active users associated with {taskCompany} are shown.
            </small>
          </div>

          {/* Selected User Confirmation Card */}
          {selectedUser && (
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 16,
              }}
              data-testid="selected-user-preview"
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: "#166534", marginBottom: 8 }}>
                <Check size={14} color="#16a34a" />
                <span>Additional Assignee Preview</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "#16a34a",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    {(selectedUser.name || "U").trim().charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <b style={{ fontSize: 13.5, color: "#14532d" }}>{selectedUser.name}</b>
                    {selectedUser.email && (
                      <div style={{ fontSize: 11.5, color: "#166534" }}>{selectedUser.email}</div>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: "#dcfce7",
                      color: "#15803d",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    {selectedUser.department || "General"}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: "#e0f2fe",
                      color: "#0369a1",
                      border: "1px solid #bae6fd",
                    }}
                  >
                    {selectedUser.company || taskCompany}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 11.5, color: "#166534", lineHeight: 1.4 }}>
                ✓ {selectedUser.name} will be added alongside <b>{originalAssigneeName}</b>. Both users will see this task and submit progress reports.
              </div>
            </div>
          )}

          {/* Handover Note / Reason */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="reassign-reason-textarea"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: 13,
                fontWeight: 600,
                color: "#1e293b",
                marginBottom: 6,
              }}
            >
              <span>
                Handover Note / Reason <span style={{ fontWeight: 400, color: "#64748b" }}>(Optional)</span>
              </span>
              {reason.length > 0 && (
                <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>
                  {reason.length} characters
                </span>
              )}
            </label>
            <textarea
              id="reassign-reason-textarea"
              className="dispatch-control"
              rows={4}
              placeholder="Explain why this task is being shared or assigned to another user (optional)."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 13,
                lineHeight: 1.5,
                color: "#0f172a",
                borderRadius: 8,
                border: "1.5px solid #cbd5e1",
                background: "#ffffff",
                resize: "vertical",
                minHeight: 96,
                boxSizing: "border-box",
                outline: "none",
              }}
              data-testid="reassign-reason-input"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                padding: "10px 12px",
                marginBottom: 16,
                fontSize: 12.5,
                color: "#b91c1c",
              }}
              data-testid="reassign-error-message"
            >
              {error}
            </div>
          )}

          {/* Modal Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              className="outline-btn"
              onClick={() => onClose(false)}
              disabled={busy}
              data-testid="reassign-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={!selectedUserId || busy}
              style={{
                background: selectedUserId ? "var(--navy)" : "#94a3b8",
                borderColor: selectedUserId ? "var(--navy)" : "#94a3b8",
                cursor: selectedUserId && !busy ? "pointer" : "not-allowed",
              }}
              data-testid="confirm-reassign-btn"
            >
              <UserPlus size={14} style={{ marginRight: 6 }} />
              {busy ? "Assigning..." : "Assign to User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
